/**
 * EPL Builder Home - Dashboard Principal
 * 
 * Funcionalidades:
 * - Lista de laboratorios creados
 * - Botón crear nuevo laboratorio
 * - Estadísticas y métricas
 * - Filtros y búsqueda
 * - Health status del sistema EPL
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Filter, 
  Eye, 
  Edit, 
  Trash2, 
  Users, 
  BarChart3,
  AlertCircle,
  CheckCircle,
  Clock,
  Beaker,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';

import { useEplLabs, useEplHealth } from '../hooks/useEpl';

const EplBuilderHome = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSystem, setFilterSystem] = useState('all');
  const [sortColumn, setSortColumn] = useState(null); // 'system', 'status', 'fields', 'date'
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  
  const { labs, loading, error, pagination, actions } = useEplLabs();
  const { health } = useEplHealth();

  // Cargar laboratorios al montar el componente
  useEffect(() => {
    actions.fetchLabs();
  }, []);

  const handleCreateLab = () => {
    navigate('/epl/builder/new');
  };

  const handleEditLab = (labId) => {
    navigate(`/epl/builder/edit/${labId}`);
  };

  const handleViewLab = (labId) => {
    navigate(`/epl/lab/${labId}`);
  };

  const handleDeleteLab = async (labId, labTitle) => {
    if (window.confirm(`¿Estás seguro de eliminar el laboratorio "${labTitle}"?`)) {
      try {
        await actions.deleteLab(labId);
      } catch (err) {
        alert(`Error al eliminar laboratorio: ${err.message}`);
      }
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      // Si ya está ordenado por esta columna, cambiar dirección
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Nueva columna, ordenar ascendente
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="w-4 h-4 text-gray-400" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-4 h-4 text-blue-600" />
      : <ArrowDown className="w-4 h-4 text-blue-600" />;
  };

  const filteredLabs = labs.filter(lab => {
    const matchesSearch = (lab.name || lab.title)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         lab.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || lab.status === filterStatus;
    const matchesSystem = filterSystem === 'all' || lab.systemCard === filterSystem;
    
    return matchesSearch && matchesFilter && matchesSystem;
  }).sort((a, b) => {
    if (!sortColumn) return 0;

    let compareValue = 0;

    switch (sortColumn) {
      case 'system':
        const systemA = (a.systemCard || 'transforma360').toLowerCase();
        const systemB = (b.systemCard || 'transforma360').toLowerCase();
        compareValue = systemA.localeCompare(systemB);
        break;
      
      case 'status':
        const statusA = (a.status || 'draft').toLowerCase();
        const statusB = (b.status || 'draft').toLowerCase();
        compareValue = statusA.localeCompare(statusB);
        break;
      
      case 'fields':
        const fieldsA = a.fields?.length || 0;
        const fieldsB = b.fields?.length || 0;
        compareValue = fieldsA - fieldsB;
        break;
      
      case 'date':
        const dateA = new Date(a.metadata?.createdAt || a.createdAt || 0);
        const dateB = new Date(b.metadata?.createdAt || b.createdAt || 0);
        compareValue = dateA - dateB;
        break;
      
      default:
        compareValue = 0;
    }

    return sortDirection === 'asc' ? compareValue : -compareValue;
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'published': return <CheckCircle className="w-4 h-4 text-green-600" />; // Mantener compatibilidad
      case 'disabled': return <AlertCircle className="w-4 h-4 text-gray-500" />;
      case 'draft': return <Clock className="w-4 h-4 text-yellow-600" />;
      case 'archived': return <AlertCircle className="w-4 h-4 text-gray-600" />;
      case 'maintenance': return <AlertCircle className="w-4 h-4 text-orange-600" />;
      default: return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Activo';
      case 'published': return 'Publicado'; // Mantener compatibilidad
      case 'disabled': return 'Deshabilitado';
      case 'draft': return 'Borrador';
      case 'archived': return 'Archivado';
      case 'maintenance': return 'Mantenimiento';
      default: return 'Desconocido';
    }
  };

  const getSystemBadge = (systemCard) => {
    const system = systemCard || 'innovafast';
    const badges = {
      // EPL-Leader Lab (Morado)
      'gestion_crisis': {
        label: 'Gestión de Crisis',
        bgColor: 'bg-purple-100',
        textColor: 'text-purple-800',
        borderColor: 'border-purple-200'
      },
      'dinamicas_equipo': {
        label: 'Dinámicas de Equipo',
        bgColor: 'bg-purple-100',
        textColor: 'text-purple-800',
        borderColor: 'border-purple-200'
      },
      'comunicacion_estrategica': {
        label: 'Comunicación Estratégica',
        bgColor: 'bg-purple-100',
        textColor: 'text-purple-800',
        borderColor: 'border-purple-200'
      },
      // EPL-Innova System (Verde/Azul/Morado)
      'innovafast': {
        label: 'InnovaFAST',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800',
        borderColor: 'border-blue-200'
      },
      'innovatech': {
        label: 'InnovaTech',
        bgColor: 'bg-indigo-100',
        textColor: 'text-indigo-800',
        borderColor: 'border-indigo-200'
      },
      'transforma360': {
        label: 'Transforma360',
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        borderColor: 'border-green-200'
      },
      // EPL-Decision System (Azul)
      'dss_builder': {
        label: 'DSS Builder',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800',
        borderColor: 'border-blue-200'
      },
      'analisis_riesgo': {
        label: 'Análisis de Riesgo',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800',
        borderColor: 'border-blue-200'
      },
      'planificacion_estrategica': {
        label: 'Planificación Estratégica',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800',
        borderColor: 'border-blue-200'
      }
    };
    
    return badges[system] || {
      label: system,
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-800',
      borderColor: 'border-gray-200'
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando laboratorios...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        
        {/* Header con título y health status */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Beaker className="w-8 h-8 text-blue-600" />
              EPL Lab Builder
            </h1>
            <p className="text-gray-600 mt-2">
              Crea laboratorios educativos interactivos con IA
            </p>
          </div>
          
          {/* Health Status */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${
                health.server === 'ok' ? 'bg-green-500' : 'bg-red-500'
              }`}></div>
              <span className="text-sm text-gray-600">
                Server: {health.server}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${
                health.database === 'connected' ? 'bg-green-500' : 'bg-red-500'
              }`}></div>
              <span className="text-sm text-gray-600">
                DB: {health.database}
              </span>
            </div>
          </div>
        </div>

        {/* Estadísticas rápidas */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Labs</p>
                <p className="text-2xl font-bold text-gray-900">{labs.length}</p>
              </div>
              <Beaker className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600">InnovaFAST</p>
                <p className="text-xl font-bold text-blue-600">
                  {labs.filter(lab => lab.systemCard === 'innovafast').length}
                </p>
              </div>
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <span className="text-blue-600 text-xs font-bold">IF</span>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600">InnovaTech</p>
                <p className="text-xl font-bold text-purple-600">
                  {labs.filter(lab => lab.systemCard === 'innovatech').length}
                </p>
              </div>
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                <span className="text-purple-600 text-xs font-bold">IT</span>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600">Transforma360</p>
                <p className="text-xl font-bold text-green-600">
                  {labs.filter(lab => lab.systemCard === 'transforma360').length}
                </p>
              </div>
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                <span className="text-green-600 text-xs font-bold">T3</span>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600">Activos</p>
                <p className="text-xl font-bold text-gray-900">
                  {labs.filter(lab => lab.status === 'active').length}
                </p>
              </div>
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        {/* Controles de búsqueda y filtros */}
        <div className="bg-white p-6 rounded-lg shadow-sm border mb-6">
          <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Buscar laboratorios..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            
            <div className="flex gap-3">
              <select
                value={filterSystem}
                onChange={(e) => setFilterSystem(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">Todos los sistemas</option>
                <optgroup label="EPL-Leader Lab">
                  <option value="gestion_crisis">Gestión de Crisis</option>
                  <option value="dinamicas_equipo">Dinámicas de Equipo</option>
                  <option value="comunicacion_estrategica">Comunicación Estratégica</option>
                </optgroup>
                <optgroup label="EPL-Innova System">
                  <option value="innovafast">InnovaFAST</option>
                  <option value="innovatech">InnovaTech</option>
                  <option value="transforma360">Transforma360</option>
                </optgroup>
                <optgroup label="EPL-Decision System">
                  <option value="dss_builder">DSS Builder</option>
                  <option value="analisis_riesgo">Análisis de Riesgo</option>
                  <option value="planificacion_estrategica">Planificación Estratégica</option>
                </optgroup>
              </select>
              
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">Todos los estados</option>
                <option value="published">Publicados</option>
                <option value="draft">Borradores</option>
                <option value="archived">Archivados</option>
              </select>
              
              <button
                onClick={handleCreateLab}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors"
              >
                <Plus className="w-5 h-5" />
                Nuevo Laboratorio
              </button>
            </div>
          </div>
        </div>

        {/* Lista de laboratorios */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Error: {error}
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border">
          {filteredLabs.length === 0 ? (
            <div className="text-center py-12">
              <Beaker className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {labs.length === 0 ? 'No hay laboratorios creados' : 'No se encontraron laboratorios'}
              </h3>
              <p className="text-gray-600 mb-6">
                {labs.length === 0 
                  ? 'Comienza creando tu primer laboratorio educativo'
                  : 'Intenta con otros términos de búsqueda'
                }
              </p>
              {labs.length === 0 && (
                <button
                  onClick={handleCreateLab}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium flex items-center gap-2 mx-auto transition-colors"
                >
                  <Plus className="w-5 h-5" />
                  Crear Primer Laboratorio
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Laboratorio
                    </th>
                    <th 
                      onClick={() => handleSort('system')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        Sistema
                        {getSortIcon('system')}
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('status')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        Estado
                        {getSortIcon('status')}
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('fields')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        Campos
                        {getSortIcon('fields')}
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('date')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        Creado
                        {getSortIcon('date')}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredLabs.map((lab) => (
                    <tr key={lab.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 max-w-md">
                        <div className="flex flex-col gap-1">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {lab.name || lab.title || 'Sin título'}
                          </div>
                          <div className="text-sm text-gray-500 line-clamp-2">
                            {lab.description || 'Sin descripción'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(() => {
                          const badge = getSystemBadge(lab.systemCard);
                          return (
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                              badge.bgColor
                            } ${
                              badge.textColor
                            } ${
                              badge.borderColor
                            }`}>
                              {badge.label}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(lab.status)}
                          <span className="text-sm text-gray-900">
                            {getStatusText(lab.status)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {lab.fields?.length || 0} campos
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(lab.metadata?.createdAt || lab.createdAt || Date.now()).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center gap-2 justify-end">
                          <button
                            onClick={() => handleViewLab(lab.id)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded transition-colors"
                            title="Ver laboratorio"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEditLab(lab.id)}
                            className="text-gray-600 hover:text-gray-900 p-1 rounded transition-colors"
                            title="Editar laboratorio"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteLab(lab.id, lab.name || lab.title || 'Sin título')}
                            className="text-red-600 hover:text-red-900 p-1 rounded transition-colors"
                            title="Eliminar laboratorio"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Paginación */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between mt-6">
            <div className="text-sm text-gray-700">
              Mostrando {filteredLabs.length} de {pagination.total} laboratorios
            </div>
            <div className="flex items-center gap-2">
              <button
                disabled={pagination.page === 1}
                onClick={() => actions.fetchLabs({ page: pagination.page - 1 })}
                className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Anterior
              </button>
              <span className="px-3 py-1 text-sm">
                Página {pagination.page} de {pagination.totalPages}
              </span>
              <button
                disabled={pagination.page === pagination.totalPages}
                onClick={() => actions.fetchLabs({ page: pagination.page + 1 })}
                className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EplBuilderHome;